import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Minus, Upload, FlaskConical, Search, CornerDownRight } from "lucide-react";
import api from "../../lib/api";
import { SortableHead, toggleSort, sortRows } from "../../components/SortableHead";
import { byDosage, dosageValue } from "../../lib/variants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: "", category: "", dosage: "", price: "", image_url: "", lab_result_url: "", coa_file_url: "", description: "", long_description: "", references: "", purity: "", lot_number: "", coa_test_date: "", coa_lab: "", stock_quantity: 0, featured: false, volume_tiers: [] };

const uploadFile = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return `${BACKEND_URL}${data.url_path}`;
};

export function ImageUploader({ value, onChange, testid, label }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      onChange(url);
      toast.success(`${label} uploaded`);
    } catch {
      toast.error(`Failed to upload ${label.toLowerCase()}`);
    } finally {
      setUploading(false);
    }
  };

  let uploadLabel = "Upload";
  if (uploading) uploadLabel = "Uploading…";
  else if (value) uploadLabel = "Replace";

  return (
    <div className="flex items-center gap-3">
      {value ? (
        <img src={value} alt={label} className="w-14 h-14 rounded object-cover border border-[#DDD7CF]" />
      ) : (
        <div className="w-14 h-14 rounded bg-[#EFEBE6] border border-dashed border-[#DDD7CF] flex items-center justify-center text-[#8C7B6B]">
          <FlaskConical className="w-5 h-5" />
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid={testid} onChange={handleFile} />
      <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => inputRef.current?.click()}>
        <Upload className="w-4 h-4 mr-1" /> {uploadLabel}
      </Button>
      {value && (
        <button type="button" className="text-xs text-red-600 hover:underline" onClick={() => onChange("")}>
          Remove
        </button>
      )}
    </div>
  );
}

export const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [sort, setSort] = useState({ field: null, dir: "asc" });

  const load = useCallback(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, category: p.category, dosage: p.dosage, price: String(p.price),
      image_url: p.image_url, lab_result_url: p.lab_result_url || "", coa_file_url: p.coa_file_url || "",
      description: p.description, long_description: p.long_description || "",
      references: (p.references || []).join("\n"),
      purity: p.purity || "", lot_number: p.lot_number || "", coa_test_date: p.coa_test_date || "", coa_lab: p.coa_lab || "",
      stock_quantity: p.stock_quantity ?? 0, featured: !!p.featured,
      volume_tiers: (p.volume_tiers || []).map((t) => ({ ...t, _key: crypto.randomUUID() })),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.category || !form.price || !form.image_url) {
      toast.error("Name, category, price and product image are required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form, price: parseFloat(form.price),
      stock_quantity: parseInt(form.stock_quantity, 10) || 0,
      lab_result_url: form.lab_result_url || null,
      coa_file_url: form.coa_file_url || null,
      purity: form.purity || null,
      lot_number: form.lot_number || null,
      coa_test_date: form.coa_test_date || null,
      coa_lab: form.coa_lab || null,
      references: form.references.split("\n").map((s) => s.trim()).filter(Boolean),
      volume_tiers: form.volume_tiers
        .map((t) => ({ min_qty: parseInt(t.min_qty, 10) || 0, percent: parseFloat(t.percent) || 0 }))
        .filter((t) => t.min_qty > 0 && t.percent > 0)
        .sort((a, b) => a.min_qty - b.min_qty),
    };
    try {
      if (editing) {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      }
      setOpen(false);
      load();
    } catch {
      toast.error("Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch {
      toast.error("Failed to delete product");
    }
  };

  const adjustStock = async (p, delta) => {
    try {
      const { data } = await api.put(`/admin/products/${p.id}/stock`, { delta });
      setProducts((prev) => prev.map((x) => (x.id === p.id ? data : x)));
    } catch {
      toast.error("Failed to adjust inventory");
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const setTier = (idx, key, val) => setForm((f) => ({
    ...f, volume_tiers: f.volume_tiers.map((t, i) => (i === idx ? { ...t, [key]: val } : t)),
  }));
  const addTier = () => setForm((f) => ({ ...f, volume_tiers: [...f.volume_tiers, { min_qty: "", percent: "", _key: crypto.randomUUID() }] }));
  const removeTier = (idx) => setForm((f) => ({ ...f, volume_tiers: f.volume_tiers.filter((_, i) => i !== idx) }));

  const q = search.trim().toLowerCase();
  const categories = [...new Set(products.map((p) => p.category))].sort();
  let visible = products;
  if (q) visible = visible.filter((p) => `${p.name} ${p.dosage} ${p.category}`.toLowerCase().includes(q));
  if (catFilter !== "all") visible = visible.filter((p) => p.category === catFilter);
  visible = sort.field
    ? sortRows(visible, sort, {
        name: (p) => `${p.name}\u0000${String(dosageValue(p.dosage)).padStart(8, "0")}`,
        category: (p) => p.category,
        price: (p) => p.price,
        stock: (p) => p.stock_quantity ?? 0,
        status: (p) => (p.in_stock ? 1 : 0),
      })
    : [...visible].sort((a, b) => a.name.localeCompare(b.name) || byDosage(a, b));
  const sizeCounts = visible.reduce((m, p) => ({ ...m, [p.name]: (m[p.name] || 0) + 1 }), {});
  const rows = visible.map((p, i) => ({ ...p, _sibling: i > 0 && visible[i - 1].name === p.name }));

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 text-[#8C7B6B] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="admin-product-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, dosage or category…"
            className="pl-9"
          />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger data-testid="admin-product-category-filter" className="w-full sm:w-[210px] bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="admin-product-category-all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c} data-testid={`admin-product-category-${c}`}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="sm:ml-auto">
          <Button data-testid="admin-add-product-button" onClick={openCreate} className="bg-[#8C7B6B] text-white hover:bg-[#75665A]">
            <Plus className="w-4 h-4 mr-1" /> Add Product
          </Button>
        </div>
      </div>

      <div className="border border-[#DDD7CF] rounded-lg bg-white overflow-x-auto">
        <Table data-testid="admin-products-table">
          <TableHeader>
            <TableRow>
              <SortableHead label="Product" active={sort.field === "name"} dir={sort.dir} onClick={() => setSort((s) => toggleSort(s, "name"))} testid="products-sort-name" />
              <SortableHead label="Category" active={sort.field === "category"} dir={sort.dir} onClick={() => setSort((s) => toggleSort(s, "category"))} testid="products-sort-category" />
              <SortableHead label="Price" active={sort.field === "price"} dir={sort.dir} onClick={() => setSort((s) => toggleSort(s, "price"))} testid="products-sort-price" />
              <SortableHead label="Inventory" active={sort.field === "stock"} dir={sort.dir} onClick={() => setSort((s) => toggleSort(s, "stock"))} testid="products-sort-stock" />
              <SortableHead label="Status" active={sort.field === "status"} dir={sort.dir} onClick={() => setSort((s) => toggleSort(s, "status"))} testid="products-sort-status" />
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-[#6E675F] py-10" data-testid="admin-no-products-match">
                  No products match.
                </TableCell>
              </TableRow>
            )}
            {rows.map((p) => (
              <TableRow key={p.id} data-testid={`admin-product-row-${p.id}`} className={p._sibling ? "border-t-0" : ""}>
                <TableCell className={p._sibling ? "py-2" : ""}>
                  {p._sibling ? (
                    <div className="flex items-center gap-3 pl-6" data-testid={`admin-product-variant-${p.id}`}>
                      <CornerDownRight className="w-4 h-4 text-[#8C7B6B] shrink-0" />
                      <img src={p.image_url} alt={p.name} className="w-7 h-7 rounded object-cover" />
                      <span className="text-[#6E675F]">{p.name} <span className="font-medium text-[#1A1A1A]">{p.dosage}</span></span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <img src={p.image_url} alt={p.name} className="w-10 h-10 rounded object-cover" />
                      <span className="font-medium">{p.name} <span className="text-[#6E675F] font-normal">{p.dosage}</span></span>
                      {sizeCounts[p.name] > 1 && (
                        <span className="text-[10px] font-semibold tracking-wide uppercase text-[#8C7B6B] bg-[#F3EFE9] border border-[#DDD7CF] rounded-none px-2 py-0.5" data-testid={`admin-product-size-count-${p.id}`}>
                          {sizeCounts[p.name]} sizes
                        </span>
                      )}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-[#6E675F]">{p.category}</TableCell>
                <TableCell className="font-medium">${p.price.toFixed(2)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button size="icon" variant="outline" className="h-7 w-7" data-testid={`admin-stock-decrease-${p.id}`} onClick={() => adjustStock(p, -1)}>
                      <Minus className="w-3.5 h-3.5" />
                    </Button>
                    <span className="w-9 text-center font-semibold tabular-nums" data-testid={`admin-stock-qty-${p.id}`}>{p.stock_quantity ?? 0}</span>
                    <Button size="icon" variant="outline" className="h-7 w-7" data-testid={`admin-stock-increase-${p.id}`} onClick={() => adjustStock(p, 1)}>
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="ml-1 text-xs text-[#8C7B6B]" data-testid={`admin-stock-add10-${p.id}`} onClick={() => adjustStock(p, 10)}>
                      +10
                    </Button>
                  </div>
                </TableCell>
                <TableCell>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-none ${p.in_stock ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`} data-testid={`admin-stock-status-${p.id}`}>
                    {p.in_stock ? "In Stock" : "Out of Stock"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" data-testid={`admin-edit-product-${p.id}`} onClick={() => openEdit(p)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`admin-delete-product-${p.id}`} onClick={() => remove(p)} className="text-red-700 border-red-200 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg uppercase tracking-wide">{editing ? "Edit Product" : "Add Product"}</DialogTitle>
            <DialogDescription>Manage product details, imagery, lab results, and inventory.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="p-name">Name</Label>
                <Input id="p-name" data-testid="product-form-name" value={form.name} onChange={set("name")} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="p-category">Category</Label>
                <Input id="p-category" data-testid="product-form-category" value={form.category} onChange={set("category")} className="mt-1" placeholder="e.g. Weight Loss" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="p-dosage">Dosage</Label>
                <Input id="p-dosage" data-testid="product-form-dosage" value={form.dosage} onChange={set("dosage")} className="mt-1" placeholder="e.g. 10mg" />
              </div>
              <div>
                <Label htmlFor="p-price">Price (CAD)</Label>
                <Input id="p-price" data-testid="product-form-price" type="number" step="0.01" value={form.price} onChange={set("price")} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="p-qty">Inventory</Label>
                <Input id="p-qty" data-testid="product-form-stock" type="number" min="0" value={form.stock_quantity} onChange={set("stock_quantity")} className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Product Image</Label>
              <div className="mt-1">
                <ImageUploader value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} testid="product-form-image-upload" label="Product image" />
              </div>
            </div>
            <div>
              <Label htmlFor="p-desc">Short Description</Label>
              <Textarea id="p-desc" data-testid="product-form-description" value={form.description} onChange={set("description")} className="mt-1" rows={2} />
            </div>
            <div>
              <Label htmlFor="p-longdesc">Full Description (blank line separates sections; a short first line becomes a heading)</Label>
              <Textarea id="p-longdesc" data-testid="product-form-long-description" value={form.long_description} onChange={set("long_description")} className="mt-1" rows={6} />
            </div>
            <div>
              <Label htmlFor="p-refs">Research References (one per line)</Label>
              <Textarea id="p-refs" data-testid="product-form-references" value={form.references} onChange={set("references")} className="mt-1" rows={3} placeholder="Study citation or link per line" />
            </div>

            <div className="border-t border-[#DDD7CF] pt-4">
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#8C7B6B] mb-3">Certificate of Analysis</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="p-purity">Purity (HPLC)</Label>
                  <Input id="p-purity" data-testid="product-form-purity" value={form.purity} onChange={set("purity")} className="mt-1" placeholder="e.g. 99.2%" />
                </div>
                <div>
                  <Label htmlFor="p-lot">Lot Number</Label>
                  <Input id="p-lot" data-testid="product-form-lot" value={form.lot_number} onChange={set("lot_number")} className="mt-1" placeholder="e.g. EB-WL-123456" />
                </div>
                <div>
                  <Label htmlFor="p-testdate">Test Date</Label>
                  <Input id="p-testdate" data-testid="product-form-testdate" type="date" value={form.coa_test_date} onChange={set("coa_test_date")} className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="p-lab">Testing Lab</Label>
                  <Input id="p-lab" data-testid="product-form-lab-name" value={form.coa_lab} onChange={set("coa_lab")} className="mt-1" placeholder="e.g. Janoshik" />
                </div>
              </div>
              <div className="mt-4">
                <Label>COA Document / Image (downloadable)</Label>
                <div className="mt-1">
                  <ImageUploader value={form.coa_file_url} onChange={(url) => setForm((f) => ({ ...f, coa_file_url: url }))} testid="product-form-coa-upload" label="COA file" />
                </div>
              </div>
            </div>

            <div className="border-t border-[#DDD7CF] pt-4" data-testid="product-volume-tiers">
              <p className="text-xs tracking-[0.15em] uppercase font-semibold text-[#8C7B6B] mb-1">Volume Discounts (This Product)</p>
              <p className="text-xs text-[#6E675F] mb-3">Leave empty to use the store-default tiers from Settings. Adding tiers here overrides the default for this product only.</p>
              <div className="space-y-2">
                {form.volume_tiers.length > 0 && (
                  <div className="grid grid-cols-[1fr_1fr_auto] gap-3 text-[10px] tracking-[0.1em] uppercase font-semibold text-[#6E675F]">
                    <span>Min Quantity</span><span>Discount %</span><span></span>
                  </div>
                )}
                {form.volume_tiers.map((t, i) => (
                  <div key={t._key || `${t.min_qty}-${t.percent}`} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-center" data-testid={`product-tier-${i}`}>
                    <Input type="number" min="1" value={t.min_qty} onChange={(e) => setTier(i, "min_qty", e.target.value)} data-testid={`product-tier-qty-${i}`} placeholder="e.g. 3" />
                    <Input type="number" min="0" step="0.5" value={t.percent} onChange={(e) => setTier(i, "percent", e.target.value)} data-testid={`product-tier-percent-${i}`} placeholder="e.g. 5" />
                    <Button size="icon" variant="outline" onClick={() => removeTier(i)} data-testid={`product-tier-remove-${i}`} className="text-red-700 border-red-200 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addTier} data-testid="product-add-tier-button">
                  <Plus className="w-4 h-4 mr-1" /> Add Tier
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch id="p-featured" data-testid="product-form-featured" checked={form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} />
              <Label htmlFor="p-featured">Best Seller</Label>
            </div>
            <p className="text-xs text-[#6E675F]">Stock status is automatic: a product shows "Out of Stock" when inventory reaches 0.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="product-form-save-button" onClick={save} disabled={saving} className="bg-[#8C7B6B] text-white hover:bg-[#75665A]">
              {saving ? "Saving…" : "Save Product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
